import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";

/**
 * Coordination network centerpiece.
 * Central SVMD node connected to 4 specialist nodes.
 * - Connection lines stroke-dash flowing inward (signals coordination).
 * - Voltage particles travel along each line on a 3-5s loop.
 * - Center node has a slow breathing pulse.
 *
 * Pure SVG + GSAP. Respects prefers-reduced-motion.
 */
const NODES = [
  { id: "cs", label: "Company\nSecretaries", x: 110, y: 110 },
  { id: "law", label: "Lawyers", x: 690, y: 110 },
  { id: "val", label: "Registered\nValuers", x: 690, y: 410 },
  { id: "spec", label: "Domain\nSpecialists", x: 110, y: 410 },
];

const CENTER = { x: 400, y: 260 };

export default function CoordinationNetwork() {
  const wrapRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const ctx = gsap.context(() => {
      // Center pulse
      gsap.to(".cn-center-ring", {
        scale: 1.18,
        opacity: 0.0,
        duration: 2.6,
        repeat: -1,
        ease: "power1.out",
        transformOrigin: "center center",
        stagger: { each: 0.9, repeat: -1 },
      });
      gsap.to(".cn-center-dot", {
        scale: 1.07,
        duration: 1.6,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        transformOrigin: "center center",
      });

      if (reduced) return;

      // Particles travel along each line from peripheral node toward center
      const particles = gsap.utils.toArray(".cn-particle");
      particles.forEach((p, i) => {
        const start = p.dataset;
        const fromX = parseFloat(start.fx);
        const fromY = parseFloat(start.fy);
        const toX = parseFloat(start.tx);
        const toY = parseFloat(start.ty);
        gsap.set(p, { attr: { cx: fromX, cy: fromY }, opacity: 0 });
        gsap.to(p, {
          attr: { cx: toX, cy: toY },
          opacity: 1,
          duration: 3.4 + (i % 4) * 0.5,
          repeat: -1,
          delay: i * 0.6,
          ease: "power1.inOut",
          keyframes: [
            { attr: { cx: fromX, cy: fromY }, opacity: 0, ease: "none" },
            { opacity: 1, duration: 0.2 },
            { attr: { cx: toX, cy: toY }, opacity: 1 },
            { opacity: 0, duration: 0.2 },
          ],
        });
      });

      // Subtle node hover float
      gsap.to(".cn-node", {
        y: "+=4",
        duration: 3,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        stagger: { each: 0.4, from: "random" },
      });
    }, wrapRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={wrapRef} className="relative w-full" data-testid="coordination-network">
      <svg
        viewBox="0 0 800 520"
        className="w-full h-auto"
        role="img"
        aria-label="Coordination network: SVMD connected to Company Secretaries, Lawyers, Registered Valuers, and Domain Specialists"
      >
        <defs>
          <radialGradient id="cn-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#D4FF3F" stopOpacity="0.55" />
            <stop offset="60%" stopColor="#D4FF3F" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#D4FF3F" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="cn-blue-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#2563EB" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#2563EB" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="cn-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#94A3B8" stopOpacity="0.2" />
            <stop offset="50%" stopColor="#94A3B8" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#FAFAF7" stopOpacity="0.5" />
          </linearGradient>
        </defs>

        {/* Faint backdrop grid */}
        <g opacity="0.08" stroke="#FAFAF7" strokeWidth="0.5">
          {Array.from({ length: 11 }).map((_, i) => (
            <line key={`h${i}`} x1="0" y1={i * 52} x2="800" y2={i * 52} />
          ))}
          {Array.from({ length: 17 }).map((_, i) => (
            <line key={`v${i}`} x1={i * 50} y1="0" x2={i * 50} y2="520" />
          ))}
        </g>

        {/* Center halo */}
        <circle cx={CENTER.x} cy={CENTER.y} r="180" fill="url(#cn-blue-glow)" />
        <circle cx={CENTER.x} cy={CENTER.y} r="120" fill="url(#cn-glow)" />

        {/* Connection lines */}
        {NODES.map((n) => (
          <line
            key={`l-${n.id}`}
            x1={n.x}
            y1={n.y}
            x2={CENTER.x}
            y2={CENTER.y}
            stroke="url(#cn-line)"
            strokeWidth="1"
            strokeLinecap="round"
            className="net-line"
          />
        ))}

        {/* Peripheral nodes */}
        {NODES.map((n) => (
          <g key={`n-${n.id}`} className="cn-node">
            <circle cx={n.x} cy={n.y} r="34" fill="#0F2847" stroke="rgba(250,250,247,0.18)" strokeWidth="1" />
            <circle cx={n.x} cy={n.y} r="34" fill="none" stroke="#FAFAF7" strokeOpacity="0.0" strokeWidth="1">
              <animate
                attributeName="stroke-opacity"
                values="0.0;0.35;0.0"
                dur="3.6s"
                repeatCount="indefinite"
                begin={`${(parseInt(n.x, 10) % 3) * 0.6}s`}
              />
              <animate attributeName="r" values="34;42;34" dur="3.6s" repeatCount="indefinite" />
            </circle>
            <circle cx={n.x} cy={n.y} r="3" fill="rgba(250,250,247,0.7)" />
            {n.label.split("\n").map((line, idx) => (
              <text
                key={idx}
                x={n.x}
                y={n.y + 60 + idx * 16}
                textAnchor="middle"
                fontFamily="Inter Variable, Inter, sans-serif"
                fontSize="13"
                fontWeight="500"
                fill="#FAFAF7"
                opacity="0.85"
              >
                {line}
              </text>
            ))}
          </g>
        ))}

        {/* Center SVMD node */}
        <g>
          <circle
            className="cn-center-ring"
            cx={CENTER.x}
            cy={CENTER.y}
            r="72"
            fill="none"
            stroke="#D4FF3F"
            strokeOpacity="0.6"
            strokeWidth="1"
          />
          <circle
            className="cn-center-ring"
            cx={CENTER.x}
            cy={CENTER.y}
            r="72"
            fill="none"
            stroke="#D4FF3F"
            strokeOpacity="0.6"
            strokeWidth="1"
          />
          <circle
            className="cn-center-dot"
            cx={CENTER.x}
            cy={CENTER.y}
            r="60"
            fill="#0A1628"
            stroke="#D4FF3F"
            strokeWidth="1.5"
          />
          <text
            x={CENTER.x}
            y={CENTER.y - 4}
            textAnchor="middle"
            fontFamily="Geist Variable, Geist, sans-serif"
            fontSize="22"
            fontWeight="600"
            fill="#FAFAF7"
            letterSpacing="-0.02em"
          >
            SVMD
          </text>
          <text
            x={CENTER.x}
            y={CENTER.y + 18}
            textAnchor="middle"
            fontFamily="Inter Variable, Inter, sans-serif"
            fontSize="10"
            fill="#D4FF3F"
            letterSpacing="0.18em"
          >
            CORE TEAM
          </text>
        </g>

        {/* Particles — soft white at 60% (voltage reserved for the SVMD core) */}
        {NODES.map((n) => (
          <circle
            key={`p-${n.id}`}
            className="cn-particle"
            cx={n.x}
            cy={n.y}
            r="3"
            fill="rgba(250,250,247,0.6)"
            data-fx={n.x}
            data-fy={n.y}
            data-tx={CENTER.x}
            data-ty={CENTER.y}
            opacity="0"
          />
        ))}
      </svg>
    </div>
  );
}
